import { useMutation, useQuery } from '@apollo/client';
import {
  Autocomplete,
  CircularProgress,
  createFilterOptions,
  Stack,
  TextField,
} from '@mui/material';
import { uniq, uniqBy } from 'lodash';
import { useMemo, useState } from 'react';
import { CREATE_ORG_SUMMARY_TAGS } from 'src/graphql/c_tags/queries';
import { GET_ALL_TAGS } from 'src/graphql/voter_tags/queries';

const filter = createFilterOptions();

export const TagsAutocomplete = ({
  organizationId,
  label,
  placeholder,
  error,
  helperText,
  value,
  onChange = () => {},
}) => {
  const { data: tagsData, refetch: refetchTags } = useQuery(GET_ALL_TAGS, {
    variables: {
      orgId: organizationId,
    },
    skip: !organizationId,
  });

  const [temp, setTemp] = useState('');
  const [createOrgTag, { loading: createOrgTagLoading }] = useMutation(CREATE_ORG_SUMMARY_TAGS);

  const tagOptions = useMemo(() => {
    const options = [];

    // Add campaign tags
    tagsData?.ri_apps_prd_c_tags?.forEach((tag) => {
      options.push({
        label: tag.title,
        value: `c_${tag.c_tag_id}`,
        type: 'c_tag',
        id: tag.c_tag_id,
        title: tag.title,
      });
    });

    // Add organization tags
    tagsData?.ri_apps_prd_org_tags?.forEach((tag) => {
      options.push({
        label: tag.title,
        value: `o_${tag.org_tag_id}`,
        type: 'org_tag',
        id: tag.org_tag_id,
        title: tag.title,
      });
    });

    return uniqBy(options, 'title');
  }, [tagsData]);

  const valueOption = useMemo(() => {
    return tagOptions.find((option) => option?.value === value);
  }, [tagOptions, value]);

  return (
    <Autocomplete
      fullWidth
      disabled={createOrgTagLoading}
      options={tagOptions}
      value={valueOption}
      loading={createOrgTagLoading}
      onChange={async (event, newValue) => {
        if (typeof newValue === 'string') {
          onChange({ label: newValue });
        } else if (newValue && newValue?.inputValue) {
          // Create a new value from the user input
          const { data } = await createOrgTag({
            variables: {
              data: [{ title: newValue.inputValue, organization_id: organizationId }],
            },
          });
          await refetchTags();

          onChange({
            label: newValue.inputValue,
            value: data?.row?.returning[0]?.org_tag_id,
            id: data?.row?.returning[0]?.org_tag_id,
            title: newValue.inputValue,
            type: 'org_tag',
          });
        } else {
          onChange(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setTemp(newInputValue);
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = options?.some((option) => inputValue === option.label);
        if (inputValue !== '' && !isExisting) {
          filtered.push({
            inputValue,
            label: `Add "${inputValue}"`,
          });
        }

        return filtered;
      }}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.label;
      }}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
      renderInput={(params) => (
        <Stack gap={0.5}>
          <TextField
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error ? error?.message : helperText}
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {createOrgTagLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        </Stack>
      )}
    />
  );
};
